import useGAnalytics, {
  EventAction,
  EventCategory,
  EventLabel,
} from "@/libs/hooks/tracking/useGAnalytics";
import useVisibilityTracker from "@/libs/hooks/tracking/useVisibilityTracking";
import { PromotionsType, usePromotions } from "@/libs/hooks/usePromotions";
import { useMainDict } from "@/libs/hooks/useTranslation";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { IconArrowLeft } from "@tabler/icons-react";
import { Accordion } from "flowbite-react";
import Image from "next/image";
import Link from "next/link";

import { isAfter, isBefore, isSameDay, isWithinInterval } from "date-fns";

type PromotionsProps = {
  showLinks?: boolean;
  openPolicy?: boolean;
  backButton?: boolean;
};

export const PromotionsSection: React.FC<PromotionsProps> = ({
  openPolicy = true,
  showLinks = true,
  backButton = true,
}) => {
  const { t } = useMainDict();
  const { promotions } = usePromotions();

  const { trackEvent } = useGAnalytics();

  useVisibilityTracker("promotions-section", () => {
    trackEvent(
      "promotions-section-visible",
      EventCategory.USER_INTERACTION,
      EventAction.BECOMES_VISIBLE,
      EventLabel.BANNER
    );
  });

  if (
    !promotions ||
    !promotions.attributes.promotions ||
    promotions.attributes.promotions.length <= 0
  ) {
    return null;
  }

  const isPromotionVisible = (promotion: any) => {
    const now = new Date();
    if (promotion.validFrom && promotion.validTo) {
      return isWithinInterval(now, {
        start: new Date(promotion.validFrom),
        end: new Date(promotion.validTo),
      });
    }
    if (promotion.validFrom) {
      return (
        isAfter(now, new Date(promotion.validFrom)) ||
        isSameDay(now, new Date(promotion.validFrom))
      );
    }
    if (promotion.validTo) {
      return (
        isBefore(now, new Date(promotion.validTo)) ||
        isSameDay(now, new Date(promotion.validTo))
      );
    }
    return true;
  };

  const visiblePromotions = promotions.attributes.promotions
    .filter(isPromotionVisible)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <section
      id="promotions-section"
      aria-labelledby="promotions-heading"
      className={`relative w-full overflow-hidden ${backButton ? "min-h-screen" : ""}`}
    >
      {/* Vintage ochre/gold background */}
      <div className="absolute inset-0 bg-gradient-to-br from-vintage-ochre via-[#C9923A] to-[#B8852F]">
        {/* Decorative pattern */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.2)_100%)]" />
      </div>

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-cream/50 to-transparent" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-8">
            {backButton && (
              <Link
                href="/"
                className="flex items-center justify-center h-12 w-12 rounded-full bg-vintage-cream text-vintage-espresso shadow-vintage hover:shadow-vintage transition-all duration-300 hover:-translate-y-1"
              >
                <IconArrowLeft size={24} />
              </Link>
            )}

            <div className="flex-1 text-center">
              {/* Decorative element */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-16 h-px bg-vintage-cream/50" />
                <span className="text-vintage-cream text-xl">✦</span>
                <div className="w-16 h-px bg-vintage-cream/50" />
              </div>

              <h1
                id="promotions-heading"
                className="font-display text-5xl md:text-6xl font-bold text-vintage-cream tracking-wide"
                style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.2)" }}
              >
                {promotions.attributes.title}
              </h1>

              {/* Decorative underline */}
              <div className="flex items-center justify-center gap-3 mt-4">
                <div className="w-10 h-px bg-vintage-cream/50" />
                <div className="w-2 h-2 bg-vintage-cream/50 rotate-45" />
                <div className="w-10 h-px bg-vintage-cream/50" />
              </div>
            </div>
          </div>
        </div>

        {/* Promotions grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {visiblePromotions.map((promotion, index) => (
            <div
              key={promotion.name}
              className="group bg-vintage-cream/95 rounded-sm shadow-vintage hover:shadow-[6px_6px_0px_rgba(0,0,0,0.25)] transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              {/* Inner border */}
              <div className="relative">
                <div className="absolute inset-2 border border-vintage-walnut/10 pointer-events-none z-10" />

                {/* Image */}
                {promotion.image && (
                  <div className="relative aspect-video overflow-hidden bg-vintage-parchment">
                    <Image
                      unoptimized={true}
                      alt={promotion.image?.altText}
                      src={promotion.image?.imageUrl}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="p-6">
                  <h2 className="font-display text-2xl font-bold text-vintage-espresso mb-3">
                    {promotion.name}
                  </h2>

                  {/* Decorative divider */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-px bg-vintage-ochre" />
                    <div className="w-1.5 h-1.5 bg-vintage-ochre rotate-45" />
                  </div>

                  <div className="font-serif text-vintage-walnut leading-relaxed">
                    <BlocksRenderer
                      blocks={{
                        paragraph: ({ children }) => (
                          <p className="mb-3">{children}</p>
                        ),
                        "list-item": ({ children }) => (
                          <li className="list-disc list-item list-inside ml-2 mb-1">
                            {children}
                          </li>
                        ),
                      }}
                      content={promotion.description}
                    />
                  </div>
                </div>

                {/* Corner decorations */}
                <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-vintage-ochre/30" />
                <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-vintage-ochre/30" />
                <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-vintage-ochre/30" />
                <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-vintage-ochre/30" />
              </div>
            </div>
          ))}
        </div>

        {/* Policy accordion */}
        <div className="bg-vintage-cream/90 rounded-sm shadow-vintage p-6 vintage-accordion">
          <Accordion
            className="border-none bg-transparent"
            collapseAll={!openPolicy}
          >
            <Accordion.Panel>
              <Accordion.Title className="text-vintage-espresso bg-transparent hover:bg-vintage-terracotta/5 focus:ring-0 font-condensed tracking-wider uppercase text-sm px-0 py-3 border-b border-vintage-ochre/30">
                {t("promotionPolicy")}
              </Accordion.Title>
              <Accordion.Content className="bg-transparent px-0 py-4">
                <div className="font-serif text-vintage-walnut leading-relaxed">
                  <BlocksRenderer
                    blocks={{
                      paragraph: ({ children }) => (
                        <p className="mb-3">{children}</p>
                      ),
                      "list-item": ({ children }) => (
                        <li className="list-disc list-item list-inside ml-2 mb-1">
                          {children}
                        </li>
                      ),
                    }}
                    content={promotions.attributes.text}
                  />
                </div>
              </Accordion.Content>
            </Accordion.Panel>
          </Accordion>
        </div>

        {/* Links */}
        {showLinks && promotions.attributes.links?.length > 0 && (
          <div className="mt-8 text-center">
            <p className="font-condensed text-vintage-cream uppercase tracking-wider text-sm mb-4">
              Learn More
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {promotions.attributes.links.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="inline-flex items-center gap-2 bg-vintage-cream text-vintage-espresso px-5 py-2 font-serif text-sm hover:bg-vintage-burgundy hover:text-vintage-cream transition-all duration-300 shadow-vintage-sm"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom decorative border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-vintage-cream/50 to-transparent" />
    </section>
  );
};
